import { useNavigate } from 'react-router-dom';
import { Star, ChevronRight } from 'lucide-react';

// HubCard — card presentazionale di una Hub. Punta a una route ESISTENTE
// (nessuna logica nuova). Tre comportamenti:
//   - card normale  → click/Enter/Spazio naviga a `card.to`
//   - card `dev`    → non cliccabile, pill "In sviluppo"
//   - ★ preferito   → bottone separato (a11y: mai bottoni annidati)
//
// Skin dai token del design system: sfondo bianco (--ds-bg), bordo, radius card,
// icona colorata con la tinta della macro-voce. fontWeight limitato a 400/500.

const cardBase = {
  background: 'var(--ds-bg)',
  border: '1px solid var(--ds-border)',
  borderRadius: 'var(--radius-card)',
  padding: 20,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 208,
  transition: 'box-shadow .18s, border-color .18s, transform .18s',
};

export default function HubCard({ card, color = 'var(--brand)', isFav = false, onToggleFav, onNavigate }) {
  const navigate = useNavigate();
  const Icon = card.icon;
  const dev = !!card.dev;

  const go = () => {
    if (dev || !card.to) return;
    if (onNavigate) onNavigate();
    navigate(card.to);
  };
  const onKey = (e) => {
    if (dev) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
  };

  const lift = (e) => {
    if (dev) return;
    e.currentTarget.style.boxShadow = '0 2px 6px rgba(31,42,40,.07),0 18px 42px rgba(31,42,40,.10)';
    e.currentTarget.style.borderColor = 'var(--border-2, var(--ds-border))';
    e.currentTarget.style.transform = 'translateY(-2px)';
  };
  const rest = (e) => {
    e.currentTarget.style.boxShadow = 'none';
    e.currentTarget.style.borderColor = 'var(--ds-border)';
    e.currentTarget.style.transform = 'none';
  };

  return (
    <div
      style={{ ...cardBase, cursor: dev ? 'default' : 'pointer', opacity: dev ? 0.85 : 1 }}
      onClick={go}
      onKeyDown={onKey}
      onMouseEnter={lift}
      onMouseLeave={rest}
      role={dev ? undefined : 'button'}
      tabIndex={dev ? undefined : 0}
      aria-label={dev ? undefined : `Apri ${card.label}`}
    >
      {/* Icona + ★ preferito */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'auto' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14, background: 'var(--chip)',
          border: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', color, flexShrink: 0,
        }}>
          <Icon size={24} strokeWidth={1.8} />
        </div>
        {!dev && card.to && onToggleFav && (
          <button
            type="button"
            aria-label={isFav ? `Rimuovi ${card.label} dai preferiti` : `Aggiungi ${card.label} ai preferiti`}
            aria-pressed={isFav}
            onClick={(e) => { e.stopPropagation(); onToggleFav(card.to); }}
            style={{
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 'var(--radius-base)',
              color: isFav ? 'var(--warn, #E8912A)' : 'var(--ds-muted)', padding: 0,
            }}
          >
            <Star size={16} strokeWidth={1.8} fill={isFav ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>

      {/* Titolo + descrizione */}
      <div className="font-display" style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginTop: 14 }}>
        {card.label}
      </div>
      {card.description && (
        <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginTop: 5, lineHeight: 1.45 }}>
          {card.description}
        </div>
      )}

      {/* Footer: pill "In sviluppo" oppure CTA + freccia */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: dev ? 'flex-start' : 'space-between', gap: 8, marginTop: 12 }}>
        {dev ? (
          <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 999, background: 'var(--chip)', color: 'var(--ds-muted)', border: '1px solid var(--ds-border)' }}>
            In sviluppo
          </span>
        ) : (
          <>
            <span style={{ fontSize: 12, fontWeight: 500, color }}>{card.cta || 'Apri'}</span>
            <ChevronRight size={16} strokeWidth={2} color={color} style={{ flexShrink: 0 }} />
          </>
        )}
      </div>
    </div>
  );
}
